import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/hooks/use-auth'
import { supabase } from '@/lib/supabase'
import { POST_STATUS } from '@/constants/socialPostStatus'
import { normalizePost } from '../api/posts'
import type { SocialPost } from '../types'
import type { RealtimeChannel } from '@supabase/supabase-js'

/**
 * Publish a post to social media via edge function
 */
export function usePublishPost() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async ({ postId, testMode = true }: { postId: string; testMode?: boolean }) => {
            const { data, error } = await supabase.functions.invoke('create-instagram-post', {
                body: { postId, testMode }
            })
            if (error) throw error
            return data
        },
        onSuccess: (data, variables) => {
            void queryClient.invalidateQueries({ queryKey: ['social-posts'] })
            toast({
                title: "✅ Post published!",
                description: variables.testMode
                    ? "Test publish completed successfully"
                    : "Your post is now live on Instagram",
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Publishing failed",
                description: error.message || "Unable to publish post. Please check your connection and try again.",
                variant: "destructive",
            })
        }
    })
}

/**
 * Process all scheduled posts that are ready to publish
 */
export function useProcessScheduledPosts() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async () => {
            const { data, error } = await supabase.functions.invoke('process-scheduled-posts')
            if (error) throw error
            return data
        },
        onSuccess: (data) => {
            void queryClient.invalidateQueries({ queryKey: ['social-posts'] })
            const processedCount = data.processed || 0
            toast({
                title: processedCount > 0 ? "✅ Posts processed!" : "ℹ️ No posts to process",
                description: processedCount > 0
                    ? `Successfully processed ${processedCount} scheduled ${processedCount === 1 ? 'post' : 'posts'}.`
                    : "No scheduled posts are ready for publishing at this time.",
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Processing failed",
                description: error.message || "Unable to process scheduled posts. Please try again.",
                variant: "destructive",
            })
        }
    })
}

/**
 * Subscribe to real-time post status updates
 */
export function usePostStatusSubscription() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    const { user } = useAuth()
    const channelRef = useRef<RealtimeChannel | null>(null)
    const processingSoundRef = useRef<HTMLAudioElement | null>(null)
    const successSoundRef = useRef<HTMLAudioElement | null>(null)

    useEffect(() => {
        if (!user?.id) return

        // Initialize notification sounds (optional)
        try {
            processingSoundRef.current = new Audio('/sounds/processing.mp3')
            successSoundRef.current = new Audio('/sounds/success.mp3')
        } catch (e) {
            console.debug('Notification sounds not available')
        }

        const channelName = `social-posts-updates:${user.id}`

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'social_posts',
                },
                async (payload) => {
                    const newPost = payload.new as SocialPost
                    const oldPost = payload.old as SocialPost

                    if (newPost.status === oldPost.status) return

                    const { data: profile } = await supabase
                        .from('social_profiles')
                        .select('*')
                        .eq('id', newPost.profile_id)
                        .single()

                    if (!profile) return

                    // Update cache
                    queryClient.setQueriesData(
                        { queryKey: ['social-posts'], exact: false },
                        (oldData: SocialPost[] | undefined) => {
                            if (!oldData) return oldData
                            return oldData.map(post =>
                                post.id === newPost.id ? normalizePost(newPost) : post
                            )
                        }
                    )

                    queryClient.setQueryData(['social-post', newPost.id], normalizePost(newPost))

                    if (oldPost.status === POST_STATUS.SCHEDULED || newPost.status === POST_STATUS.SCHEDULED) {
                        queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] })
                    }

                    // Show notifications
                    switch (newPost.status) {
                        case POST_STATUS.PROCESSING:
                            processingSoundRef.current?.play().catch(() => { })
                            toast({
                                title: "📤 Publishing post...",
                                description: `Your post is being published to ${profile.platform}`,
                            })
                            break

                        case POST_STATUS.POSTED:
                            successSoundRef.current?.play().catch(() => { })
                            toast({
                                title: "✅ Post published successfully!",
                                description: newPost.platform_specific_data?.instagram_post_id
                                    ? `Your post is now live on ${profile.platform}`
                                    : `Your post is now live on ${profile.platform}`,
                            })
                            break

                        case POST_STATUS.ERROR:
                            toast({
                                title: "❌ Publishing failed",
                                description: newPost.error_message || "Failed to publish post. Please try again.",
                                variant: "destructive",
                            })
                            break

                        case POST_STATUS.SCHEDULED:
                            if (oldPost.status === POST_STATUS.ERROR || oldPost.status === POST_STATUS.PROCESSING) {
                                toast({
                                    title: "🕐 Post rescheduled",
                                    description: `Post scheduled for ${new Date(newPost.scheduled_time!).toLocaleString()}`,
                                })
                            }
                            break
                    }
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.debug('Subscribed to post status updates')
                } else if (status === 'CHANNEL_ERROR') {
                    console.error('Failed to subscribe to post status updates')
                    setTimeout(() => {
                        if (channelRef.current) {
                            channelRef.current.subscribe()
                        }
                    }, 5000)
                }
            })

        channelRef.current = channel

        return () => {
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current)
                channelRef.current = null
            }
        }
    }, [user?.id, queryClient, toast])

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && channelRef.current?.state === 'joined') {
                queryClient.invalidateQueries({ queryKey: ['social-posts'] })
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [queryClient])

    const checkForUpdates = async () => {
        await queryClient.invalidateQueries({ queryKey: ['social-posts'] })
    }

    return {
        isConnected: channelRef.current?.state === 'joined',
        checkForUpdates
    }
}
